import { useState } from "react";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { requestQuotationExtension } from "api";
import { quoteRequestQueryKeys } from "api/queries/quotes/quoteRequestQueryKeys";
import Button from "components/v2/Button/Button";
import QuoteExtensionDialog from "pages/shipper/ShipmentQuotes/components/QuoteExtensionDialog";

interface QuotationFooterRequestExtensionProps {
  hasAlreadyRequestedExtension: boolean;
  quotation: any;
}

export default function QuotationFooterRequestExtension({
  hasAlreadyRequestedExtension,
  quotation,
}: QuotationFooterRequestExtensionProps) {
  const { id } = useParams();
  const queryClient = useQueryClient();
  const intl = useIntl();
  const [showQuoteExtensionDialog, setQuoteExtensionDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const handleClickRequestQuoteExtension = () => {
    setQuoteExtensionDialog(true);
  };

  const handleCloseRequestQuoteExtension = () => {
    setQuoteExtensionDialog(false);
    setErrorMessage("");
  };

  const refetchQuotationList = () =>
    queryClient.invalidateQueries({ queryKey: quoteRequestQueryKeys.quoteQuotationListing(id!) });

  const { mutate: requestExtension, isLoading: isRequestingExtension } = useMutation({
    mutationFn: () => requestQuotationExtension(quotation.reference),
    onSuccess: () => {
      handleCloseRequestQuoteExtension();
      refetchQuotationList();
    },
    onError: (error: any) => {
      setErrorMessage(JSON.stringify(error.response.data.detail));
    },
  });

  return (
    <>
      <QuoteExtensionDialog
        quoteExtensionDialog={showQuoteExtensionDialog}
        closeQuoteExtensionModal={handleCloseRequestQuoteExtension}
        submitQuoteExtension={requestExtension}
        errorMessage={errorMessage}
        isRequestingExtension={isRequestingExtension}
      />
      <Button
        label={
          hasAlreadyRequestedExtension
            ? intl.formatMessage({
                id: "shipment-quotes.components.quotes.extensionRequested",
                defaultMessage: "Extension requested",
              })
            : intl.formatMessage({
                id: "shipment-quotes.components.quotes.requestExtension",
                defaultMessage: "Request extension",
              })
        }
        disabled={hasAlreadyRequestedExtension}
        onClick={handleClickRequestQuoteExtension}
      />
    </>
  );
}
